import { FC, ReactNode, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import './pageloader.css';

// Encryption utilities (restored from your original code)
const deriveKey = async (uid: string): Promise<CryptoKey> => {
  const encoder = new TextEncoder();
  const salt = encoder.encode('static-salt-for-demo');
  const baseKey = encoder.encode(uid + ':auth-key');
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    baseKey,
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
};

const decryptData = async (encryptedData: string, key: CryptoKey): Promise<string> => {
  const [ivHex, encryptedHex] = encryptedData.split(':');
  if (!ivHex || !encryptedHex) throw new Error('Invalid encrypted data format');
  const iv = new Uint8Array(ivHex.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16)));
  const encrypted = new Uint8Array(encryptedHex.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16)));
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    encrypted
  );
  return new TextDecoder().decode(decrypted);
};

interface PageLoaderProps {
  children: ReactNode;
  isContentReady: boolean;
  onNetworkErrorChange?: (isNetworkError: boolean) => void;
}

interface CachedAuth {
  uid: string;
  user: {
    id: string;
    email: string | null;
    selectedAffiliation: { role: string; schoolID: string } | null;
    affiliations: Array<{ role: string; schoolID: string }>;
    firstName: string;
    lastName: string;
  };
  expiry: number;
}

const PageLoader: FC<PageLoaderProps> = ({ children, isContentReady, onNetworkErrorChange }) => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [isStable, setIsStable] = useState<boolean>(false);
  const { toast } = useToast();

  const publicRoutes = [
    '/',
    '/landing-page',
    '/privacy-policy',
    '/terms-of-service',
    '/admin-login',
    '/faculty-login',
    '/guardian-login',
    '/student-login',
    '/school-admin-sign-up',
    '/portal-selection',
    '/owner-login',
  ].map(route => route.toLowerCase());

  useEffect(() => {
    const updateOnlineStatus = () => {
      const online = navigator.onLine;
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] PageLoader - Network status: ${online ? 'Online' : 'Offline'}`);
      setIsOnline(online);
      if (onNetworkErrorChange) {
        onNetworkErrorChange(!online);
      }
    };

    // Debounce initial check for stability
    const timer = setTimeout(() => {
      updateOnlineStatus();
      setIsStable(true);
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] PageLoader - Network status stabilized: ${navigator.onLine ? 'Online' : 'Offline'}`);
    }, 500);

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, [onNetworkErrorChange]);

  // Show retry button if offline and stable
  if (!isOnline && isStable) {
    console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] PageLoader - Offline and stable, showing retry button`);
    return (
      <div className="page-loader">
        <div className="offline-message">
          Offline. Please reconnect to the internet and try again.
          <Button
            className="mt-1"
            onClick={() => {
              console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] PageLoader - Retry button clicked`);
              window.location.reload();
            }}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Show spinner if not stable or content not ready
  if (!isStable || !isContentReady) {
    console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] PageLoader - Not stable or content not ready, showing spinner`);
    return (
      <div className="page-loader">
        <div className="spinner" />
      </div>
    );
  }

  // State for cached auth validity
  const [hasValidCache, setHasValidCache] = useState<boolean>(false);

  useEffect(() => {
    const checkCachedAuth = async () => {
      const encryptedAuth = localStorage.getItem('cachedAuth');
      if (encryptedAuth && isOnline) {
        try {
          const cachedData = JSON.parse(encryptedAuth) as { uid: string; data: string };
          const key = await deriveKey(cachedData.uid);
          const decryptedAuth = await decryptData(cachedData.data, key);
          const { expiry } = JSON.parse(decryptedAuth) as CachedAuth;
          if (Date.now() < expiry + 30 * 60 * 1000) {
            console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] PageLoader - Valid cache found`);
            setHasValidCache(true);
          } else {
            console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] PageLoader - Cached token expired`);
            localStorage.removeItem('cachedAuth');
            setHasValidCache(false);
          }
        } catch (err) {
          console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] PageLoader - Failed to decrypt cached auth:`, err);
          setHasValidCache(false);
        }
      } else {
        setHasValidCache(false);
      }
    };

    checkCachedAuth();
  }, [isOnline]);

  // Show permission error for non-public routes without valid cache when online
  useEffect(() => {
    if (isOnline && !hasValidCache && !publicRoutes.includes(window.location.pathname.toLowerCase())) {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] PageLoader - No valid cache for protected route`);
      toast({
        title: 'Authentication Error',
        description: 'No valid session found. Please log in again.',
        variant: 'destructive',
        duration: 5000,
      });
    }
  }, [isOnline, hasValidCache, publicRoutes, toast]);

  if (isOnline && !hasValidCache && !publicRoutes.includes(window.location.pathname.toLowerCase())) {
    return (
      <div className="page-loader">
        <div className="offline-message">
          🚫 No valid permissions to access this page.
          <Button className="mt-4" asChild>
            <a href="/admin-login">Go to Login</a>
          </Button>
        </div>
      </div>
    );
  }

  // Render content if online, stable, and content ready
  console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] PageLoader - Online, stable, and content ready, rendering children`);
  return <>{children}</>;
};

export default PageLoader;